import { shapeProjectile } from './shapes.ts';
import { ARSENAL, BRANCHES, TITAN_EYE, ENCHANT_DAMAGE_BP, ENCHANT_FX, GROUND, HERO_DMG_TYPE, LAND, NIGHTMARES, PROJECTILE_DMG_TYPE, RELIC_AWAKENED_FX, RELIC_FX, STRUCTURES, TREBUCHET, type BranchDef, type DamageType, type Enchant, type StructureDef, type StructLevel } from '@somnhold/content';
import { awake, bannerBp, gearAfterHit } from './gear.ts';
import { cache, cellId } from '../grid.ts';
import { hexDist, hexToWorld, worldToHex } from '../hex.ts';
import { idiv, imax, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Projectile, ProjectileKind, Structure } from '../types.ts';
import { groveRoots, heroHitExtras, templeBp } from './kits.ts';
import { lampHeal } from './uniques.ts';
import { arrowLanded } from './panoply-ranger.ts';
import { sanctumBless, towerRange } from './vigil.ts';
import { turnedShot } from './unsleeping/turned.ts';
import { frostReload } from './frost.ts';
import { damageEnemy, damageStructure, enemiesNear, ev, findEnemy, findHero, gearOnHit, isFlying, progressKey, targetable } from './combat.ts';

/** Fire-rate bonus (bp) a tower gets from beacons, Ingenuity and War Horns. Beacons don't stack. */
const fireRateBonus = (s: BattleState, st: Structure): number => {
  const c = hexToWorld(st.q, st.r);
  let beacon = 0;
  for (const b of s.structures) {
    if (b.destroyed || b.type !== 'beacon' || b.id === st.id) continue;
    const bc = hexToWorld(b.q, b.r);
    if (!withinRange(c.x, c.y, bc.x, bc.y, STRUCTURES.beacon.range)) continue;
    const bp = (STRUCTURES.beacon.auraBp as [number, number, number, number, number, number])[b.level - 1] as number;
    if (bp > beacon) beacon = bp;
  }
  let horn = 0;
  for (const h of s.heroes) {
    if (h.downTicks > 0 || !h.relics.includes('warHorn')) continue;
    const fx = awake(h, 'warHorn') ? RELIC_AWAKENED_FX.warHorn : RELIC_FX.warHorn;
    if (fx.fireRateBp > horn && withinRange(c.x, c.y, h.x, h.y, fx.range)) horn = fx.fireRateBp;
  }
  // An arsenal touching it (era III, terraced land): with a beacon, the two stop at their cap.
  const arsenal = arsenalBp(s, st);
  const aura = arsenal > 0 ? (beacon + arsenal > ARSENAL.capBp ? (beacon > ARSENAL.capBp ? beacon : ARSENAL.capBp) : beacon + arsenal) : beacon;
  return aura + horn + (st.buffTicks > 0 ? st.buffBp : 0);
};

/**
 * The towers an arsenal touches, worked out once per fight (what touches what doesn't change while it lasts;
 * a fallen arsenal is checked when asked).
 */
const ARSENALS = new WeakMap<Structure[], Map<number, Structure[]>>();
const arsenalBp = (s: BattleState, st: Structure): number => {
  if (!ARSENAL.towers.includes(st.type)) return 0;
  let m = ARSENALS.get(s.structures);
  if (!m) {
    m = new Map();
    for (const a of s.structures) {
      if (a.type !== 'arsenal') continue;
      const cells = [{ q: a.q, r: a.r }, ...(a.foot ?? [])];
      for (const t of s.structures) {
        if (!ARSENAL.towers.includes(t.type)) continue;
        if (cells.some((c) => hexDist(c, t) === 1)) m.set(t.id, [...(m.get(t.id) ?? []), a]);
      }
    }
    ARSENALS.set(s.structures, m);
  }
  return (m.get(st.id) ?? []).some((a) => !a.destroyed) ? ARSENAL.fireRateBp : 0;
};

const effectiveReload = (base: number, bonusBp: number): number => {
  const r = idiv(base * 10000, 10000 + bonusBp);
  return r < 1 ? 1 : r;
};

/** A frost colossus's cold around a tower: it reloads this much slower (bp; the strongest cold near it counts). */
const chillBp = (s: BattleState, st: Structure): number => {
  const c = hexToWorld(st.q, st.r);
  let bp = 10000;
  for (const e of s.enemies) {
    const ch = NIGHTMARES[e.kind].chills;
    if (!ch || e.hp <= 0 || ch.reloadBp <= bp) continue;
    if (withinRange(c.x, c.y, e.x, e.y, ch.radius)) bp = ch.reloadBp;
  }
  return bp;
};

/** Chooses the nightmare closest to the keep (lowest cost-to-go) within range; with `heavyFirst` (the trebuchet), a big one before any other. */
const pickTarget = (
  s: BattleState,
  x: number,
  y: number,
  range: number,
  ground: boolean,
  air: boolean,
  flow: Int32Array,
  heavyFirst = false,
): Enemy | null => {
  let best: Enemy | null = null;
  let bk = 0x7fffffff;
  let bh = false;
  for (const e of enemiesNear(s, x, y, range)) {
    if (e.kind === 'boneWall' && !ground) continue;
    const fl = isFlying(e);
    if ((fl && !air) || (!fl && !ground)) continue;
    if (!targetable(s, e)) continue;
    const h = heavyFirst && isHeavy(e);
    if (bh && !h) continue;
    const k = progressKey(s, e, flow);
    if (k < bk || (h && !bh)) {
      bk = k;
      best = e;
      bh = h;
    }
  }
  return best;
};

/** A tower a wyvern set burning: it loses a little every second, unless a hero stands by and beats the fire out. */
const burnTick = (s: BattleState, st: Structure): void => {
  const c = hexToWorld(st.q, st.r);
  if (s.heroes.some((h) => h.downTicks === 0 && withinRange(h.x, h.y, c.x, c.y, 1100))) {
    st.burnTicks = 0;
    ev(s, { t: 'boom', x: c.x, y: c.y, r: 500, kind: 'douse' });
    return;
  }
  st.burnTicks = (st.burnTicks ?? 1) - 1;
  if (st.burnTicks % 12 === 0) damageStructure(s, st, st.burnDmg ?? 0, null, 'fire');
};

/**
 * What fires from a structure, or null: a tower as itself, or a wall run with a tower mounted on it (the adarve,
 * terraced land), which shoots as that tower from the top of the wall. Everything that acts on towers (sleep, silk,
 * curses, caps, the general's reckoning) asks this, not the category.
 */
export const firesAs = (st: Structure): { def: StructureDef; level: StructLevel; mounted: boolean } | null => {
  if (st.mount) return { def: STRUCTURES[st.mount.type], level: st.mount.level, mounted: true };
  const def = STRUCTURES[st.type];
  return def.category === 'tower' ? { def, level: st.level, mounted: false } : null;
};
export const firesAsTower = (st: Structure): boolean => !!st.mount || STRUCTURES[st.type].category === 'tower';

/** A level 4 tower's branch on terraced land (docs/OBRAS.md), or null. */
const branchOf = (st: Structure): BranchDef | null => (st.branch ? BRANCHES[st.branch] : null);

/** Whether nothing silences this one: a sealed clockwork turret. */
export const sealedTower = (st: Structure): boolean => !!st.branch && !!BRANCHES[st.branch].sealed;

/** Pitch and embers: the burning patch where the shot falls (one per tower, refreshed; never on water). */
const burnGround = (s: BattleState, st: Structure, b: BranchDef, x: number, y: number, dmg: number): void => {
  const f = b.burn;
  if (!f) return;
  const land = s.map.land;
  if (land) {
    const h = worldToHex(x, y);
    const k = land.kind[cellId(s.map.radius, h.q, h.r)];
    if (k === GROUND.water || k === GROUND.ford) return;
  }
  const z = st.burnZone ? s.zones.find((o) => o.id === st.burnZone) : undefined;
  if (z) {
    z.x = x;
    z.y = y;
    z.ticks = f.ticks;
    return;
  }
  const id = s.nextId++;
  s.zones.push({ id, kind: 'burn', x, y, radius: f.radius, cells: [], ticks: f.ticks, every: f.every, damage: mulBp(dmg, f.dmgBp), slowBp: 0, pid: '' });
  st.burnZone = id;
};

/** The harpoon's line: every nightmare on the ground or in the air within `width` of the shot, out to the tower's reach. */
const harpoonLine = (s: BattleState, st: Structure, b: BranchDef, x: number, y: number, tgt: Enemy, reach: number, dmg: number): void => {
  const ln = b.line;
  if (!ln) return;
  const dx = tgt.x - x;
  const dy = tgt.y - y;
  const d = isqrt(dx * dx + dy * dy);
  if (d <= 0) return;
  // The far end of the line: the target's direction, out to the tower's reach.
  const ex = x + idiv(dx * reach, d);
  const ey = y + idiv(dy * reach, d);
  const len2 = reach * reach;
  for (const e of enemiesNear(s, x, y, reach)) {
    if (e.id === tgt.id || !targetable(s, e)) continue;
    // Distance from the line, by projection onto it (integers only).
    const px = e.x - x;
    const py = e.y - y;
    const dot = px * (ex - x) + py * (ey - y);
    if (dot < 0 || dot > len2) continue;
    const cross = px * (ey - y) - py * (ex - x);
    if ((cross < 0 ? -cross : cross) > ln.width * reach) continue;
    const big = isFlying(e) || !!NIGHTMARES[e.kind].big;
    damageEnemy(s, e, big ? mulBp(dmg, ln.bigBp) : dmg, { pid: '', pierce: true });
  }
  void st;
};

/**
 * The Titan's Eye: it gathers charge from the nightmares in its reach and, full, fires by itself at the biggest one
 * there. Nothing silences it, and its blow comes from no side (no plate or shield turns it).
 */
const titanEye = (s: BattleState, st: Structure): void => {
  if (s.tick % TITAN_EYE.every !== 0) return;
  const c = hexToWorld(st.q, st.r);
  const near = enemiesNear(s, c.x, c.y, STRUCTURES.titanEye.range, (e) => targetable(s, e));
  st.charge = (st.charge ?? 0) + near.length;
  if (st.charge < TITAN_EYE.full || near.length === 0) return;
  let best = near[0] as Enemy;
  for (const e of near) if (e.maxHp > best.maxHp) best = e;
  st.charge = 0;
  st.aim = best.id;
  ev(s, { t: 'shot', from: st.id, to: best.id, kind: 'arcane', x: c.x, y: c.y, tx: best.x, ty: best.y });
  damageEnemy(s, best, TITAN_EYE.damage, { pid: '', ignoresArmor: true, type: 'holy' });
};

export const updateStructures = (s: BattleState): void => {
  const flow = cache(s).flow;
  for (const st of s.structures) {
    if (st.destroyed) continue;
    if (st.buffTicks > 0) st.buffTicks--;
    if ((st.burnTicks ?? 0) > 0) burnTick(s, st);
    if (st.destroyed) continue;
    groveRoots(s, st);
    sanctumBless(s, st);
    if (st.type === 'titanEye') {
      titanEye(s, st);
      continue;
    }
    // A tower, or a tower mounted on a wall run (the adarve); a mounted one shoots from the top of the wall.
    const fire = firesAs(st);
    if (!fire || fire.def.type === 'beacon') continue;
    const def = fire.def;
    const br = branchOf(st);
    // A sealed clockwork turret shrugs off sleep, silk and curses.
    const sealed = !!br?.sealed;
    if (st.cursed && !sealed) continue;
    // Asleep under a lidless dreamer's open eyes.
    if ((st.dozeUntil ?? 0) > s.tick && !sealed) continue;
    // Cocooned by a weaver: silent until the silk gives, much sooner with a hero cutting it.
    if ((st.cocoon ?? 0) > 0 && !sealed) {
      const w = hexToWorld(st.q, st.r);
      const silk = NIGHTMARES.weaver.weaves;
      const cut = s.heroes.some((h) => h.downTicks === 0 && withinRange(h.x, h.y, w.x, w.y, 1100));
      st.cocoon = imax(0, (st.cocoon ?? 0) - (cut && silk ? idiv(silk.ticks, silk.cutTicks) : 1));
      if (st.cocoon === 0) ev(s, { t: 'boom', x: w.x, y: w.y, r: 500, kind: 'cut' });
      continue;
    }
    if (st.cd > 0) {
      st.cd--;
      continue;
    }
    // Beside (a bet): Hilde's towers shoot only with a hero standing by them.
    if (s.rules?.beside && !manned(s, st, s.rules.beside)) continue;
    const c = hexToWorld(st.q, st.r);
    const lvl = fire.level - 1;
    let dmg = def.damage[lvl] as number;
    if (br?.damageBp) dmg = mulBp(dmg, br.damageBp);
    if (st.enchant) dmg = mulBp(dmg, 10000 + ENCHANT_DAMAGE_BP);
    // A hero's banner nearby (rare gear) and the blessed oil offered at the keep.
    const banner = bannerBp(s, st) + (s.rules?.towerDamageBp ?? 0);
    if (banner) dmg = mulBp(dmg, 10000 + banner);
    if (s.rules?.builtBp) dmg = mulBp(dmg, s.rules.builtBp);
    // Taken by Bruni's forge: it shoots the heroes (battle/unsleeping/turned.ts).
    // Its reach (a branch's, and from the top of a wall one level higher) and its reload (a branch may slow it, and
    // so may the frost, battle/frost.ts).
    const reach = towerRange(s, st, def.range + (br?.rangeAdd ?? 0) + (fire.mounted ? LAND.levelRange : 0));
    const reload = (): number => frostReload(s, st, effectiveReload(def.reload, fireRateBonus(s, st)), chillBp(s, st), br?.reloadBp ?? 10000);
    if ((st.turnedUntil ?? 0) > s.tick && !sealed) {
      if (turnedShot(s, st, c.x, c.y, reach, dmg)) st.cd = reload();
      continue;
    }
    if (def.type === 'belfry') {
      const hits = enemiesNear(s, c.x, c.y, def.splash, (e) => !isFlying(e) && e.kind !== 'boneWall');
      if (hits.length === 0) continue;
      const stun = (def.stun as [number, number, number, number, number, number])[lvl] as number;
      for (const e of hits) {
        damageEnemy(s, e, dmg, { pid: '' });
        if (e.stun < stunFor(e, stun)) e.stun = stunFor(e, stun);
      }
      ev(s, { t: 'bell', id: st.id });
      st.cd = frostReload(s, st, effectiveReload(def.reload, fireRateBonus(s, st)), chillBp(s, st), 10000);
      continue;
    }
    // A watchtower nearby (era IV): it reaches farther.
    // The sky branch shoots only what flies.
    const tgt = pickTarget(s, c.x, c.y, reach, def.targetsGround && !br?.airOnly, def.targetsAir, flow, def.type === 'trebuchet');
    if (!tgt) continue;
    // A temple nearby: the inked and the undead take more.
    dmg = mulBp(dmg, templeBp(s, st, tgt));
    st.aim = tgt.id;
    st.cd = reload();
    if (def.type === 'arcane') {
      ev(s, { t: 'shot', from: st.id, to: tgt.id, kind: 'arcane', x: c.x, y: c.y, tx: tgt.x, ty: tgt.y });
      hitWithEnchant(s, tgt, dmg, st.enchant, true, false, '', false, br?.dmgType ?? 'lightning', c.x, c.y);
      continue;
    }
    const kind: ProjectileKind = def.type === 'catapult' ? 'rock' : def.type === 'trebuchet' ? 'boulder' : 'bolt';
    const shots = br?.shots ?? 1;
    for (let k = 0; k < shots; k++) {
      launch(s, {
        kind,
        x: c.x,
        y: c.y,
        tx: tgt.x,
        ty: tgt.y,
        target: kind === 'bolt' ? tgt.id : 0,
        speed: def.projectileSpeed,
        damage: dmg,
        splash: br?.splash ?? def.splash,
        ignoresArmor: def.ignoresArmor,
        pierce: kind === 'bolt',
        enchant: st.enchant,
        stun: 0,
        pid: '',
        hostile: false,
      });
    }
    // Split stones: more falls round the first, each lighter.
    const sp = br?.split;
    if (sp) {
      for (let k = 0; k < sp.n; k++) {
        const off = k % 2 === 0 ? sp.spread : -sp.spread;
        launch(s, { kind, x: c.x, y: c.y, tx: tgt.x + off, ty: tgt.y + (k < 2 ? -off : off), target: 0, speed: def.projectileSpeed, damage: mulBp(dmg, sp.damageBp), splash: def.splash, ignoresArmor: def.ignoresArmor, pierce: false, enchant: st.enchant, stun: 0, pid: '', hostile: false });
      }
    }
    if (br?.line) harpoonLine(s, st, br, c.x, c.y, tgt, reach, dmg);
    if (br?.burn) burnGround(s, st, br, tgt.x, tgt.y, dmg);
    ev(s, { t: 'shot', from: st.id, to: tgt.id, kind, x: c.x, y: c.y, tx: tgt.x, ty: tgt.y });
  }
};

export const isBoss = (e: Enemy): boolean => e.kind === 'witch' || e.kind === 'shell';

/** The big ones a trebuchet's stone hits hardest: bosses, the Warden, warlords, giants, colossi and siege engines. */
export const isHeavy = (e: Enemy): boolean =>
  isBoss(e) || e.elite || e.kind === 'warden' || e.kind === 'magmaLord' || e.kind === 'colossus' || !!NIGHTMARES[e.kind].big || !!NIGHTMARES[e.kind].siege;

/** Bosses, the Warden, elites (warlords) and siege rams can't be stunned. */
export const stunFor = (e: Enemy, stun: number): number => {
  // The dragon of the Burn is never stunned, pulled nor pushed (only its harpoons bring it down): a stun on it is a dry
  // blow on its glass (battle/dragon.ts), taken on its next tick. A probe of 1 or 3 (whether it can be moved, whether
  // it is big) only asks: every real stun is longer (6 ticks or more).
  if (e.kind === 'dragon') {
    if (stun > 3) e.dry = true;
    return 0;
  }
  return stunOf(e, stun);
};
const stunOf = (e: Enemy, stun: number): number =>
  (e.un?.unstoppable ?? 0) > 0 || isBoss(e) || e.kind === 'warden' || e.kind === 'magmaLord' || e.kind === 'colossus' || e.elite || NIGHTMARES[e.kind].siege ? 0 : NIGHTMARES[e.kind].big ? idiv(stun, 3) : stun;

/** ...and slows only half work on them. */
export const slowFor = (e: Enemy, slowBp: number): number => (isBoss(e) || e.kind === 'warden' || e.kind === 'magmaLord' || e.kind === 'colossus' || e.elite || NIGHTMARES[e.kind].siege || NIGHTMARES[e.kind].big ? idiv(slowBp, 2) : slowBp);

export const updateTurrets = (s: BattleState): void => {
  const flow = cache(s).flow;
  for (const t of s.turrets) if (t.hp !== undefined && t.hp <= 0) ev(s, { t: 'boom', x: t.x, y: t.y, r: 500, kind: 'turretBroken' });
  s.turrets = s.turrets.filter((t) => t.expires > s.tick && (t.hp === undefined || t.hp > 0));
  for (const t of s.turrets) {
    if (t.cd > 0) {
      t.cd--;
      continue;
    }
    const tgt = pickTarget(s, t.x, t.y, t.range, true, true, flow);
    if (!tgt) continue;
    t.aim = tgt.id;
    t.cd = t.reload;
    const owner = s.heroes.find((h) => h.id === t.owner);
    launch(s, {
      kind: 'bullet',
      x: t.x,
      y: t.y,
      tx: tgt.x,
      ty: tgt.y,
      target: tgt.id,
      speed: 1500,
      damage: t.damage,
      splash: 0,
      ignoresArmor: false,
      pierce: true,
      enchant: t.enchant ?? null,
      stun: 0,
      pid: owner ? owner.pid : '',
      hostile: false,
      ...(t.dt ? { dt: t.dt } : {}),
    });
    ev(s, { t: 'shot', from: t.id, to: tgt.id, kind: 'bullet', x: t.x, y: t.y, tx: tgt.x, ty: tgt.y });
  }
};

export const hitWithEnchant = (
  s: BattleState,
  e: Enemy,
  dmg: number,
  enchant: Enchant | null,
  ignoresArmor: boolean,
  pierce: boolean,
  pid: string,
  crit?: boolean,
  type: DamageType = 'physical',
  fromX?: number,
  fromY?: number,
  bolt?: boolean,
): number => {
  // An enchanted shot deals its element.
  const dt: DamageType = enchant ?? type;
  const dealt = damageEnemy(s, e, dmg, { pid, ignoresArmor, pierce, ...(crit ? { crit } : {}), ...(dt !== 'physical' ? { type: dt } : {}), ...(fromX !== undefined && fromY !== undefined ? { fromX, fromY } : {}), ...(bolt ? { bolt } : {}) });
  if (!enchant || e.hp <= 0) return dealt;
  if (enchant === 'fire') {
    // (The dragon's glass never takes a burn: battle/dragon.ts.)
    if (e.id === s.dragon?.id) return dealt;
    e.dotDmg = ENCHANT_FX.fire.dot;
    e.dotType = undefined;
    e.dotEvery = ENCHANT_FX.fire.every;
    e.dotTicks = ENCHANT_FX.fire.ticks;
  } else if (enchant === 'acid') {
    if ((e.acidTicks ?? 0) < ENCHANT_FX.acid.ticks) e.acidTicks = ENCHANT_FX.acid.ticks;
  } else if (enchant === 'frost') {
    if (e.slowBp < slowFor(e, ENCHANT_FX.frost.slowBp)) e.slowBp = slowFor(e, ENCHANT_FX.frost.slowBp);
    if (e.slowTicks < ENCHANT_FX.frost.ticks) e.slowTicks = ENCHANT_FX.frost.ticks;
  } else {
    // Lightning arcs to the next closest nightmares.
    let from = e;
    const hit = new Set<number>([e.id]);
    for (let i = 0; i < ENCHANT_FX.lightning.chains; i++) {
      let best: Enemy | null = null;
      let bd = 0x7fffffff;
      for (const o of enemiesNear(s, from.x, from.y, ENCHANT_FX.lightning.chainRange)) {
        if (hit.has(o.id) || !targetable(s, o)) continue;
        const dx = o.x - from.x;
        const dy = o.y - from.y;
        const dd = dx * dx + dy * dy;
        if (dd < bd) {
          bd = dd;
          best = o;
        }
      }
      if (!best) break;
      hit.add(best.id);
      ev(s, { t: 'shot', from: from.id, to: best.id, kind: 'chain', x: from.x, y: from.y, tx: best.x, ty: best.y });
      damageEnemy(s, best, mulBp(dmg, ENCHANT_FX.lightning.chainBp), { pid, ignoresArmor: true, type: 'lightning' });
      from = best;
    }
  }
  return dealt;
};

type Launch = Omit<Projectile, 'id' | 'sx' | 'sy' | 'flight' | 't'>;

export const launch = (s: BattleState, p: Launch): Projectile => {
  const dx = p.tx - p.x;
  const dy = p.ty - p.y;
  const d = isqrt(dx * dx + dy * dy);
  const flight = p.speed > 0 ? idiv(d + p.speed - 1, p.speed) : 1;
  const proj: Projectile = shapeProjectile({ ...p, id: s.nextId++, sx: p.x, sy: p.y, flight: flight < 1 ? 1 : flight, t: 0 });
  s.projectiles.push(proj);
  return proj;
};

export const updateProjectiles = (s: BattleState): void => {
  const keep: Projectile[] = [];
  for (const p of s.projectiles) {
    p.t++;
    if (p.target > 0) {
      const e = findEnemy(s, p.target);
      if (e && e.hp > 0) {
        p.tx = e.x;
        p.ty = e.y;
      }
      const dx = p.tx - p.x;
      const dy = p.ty - p.y;
      const d = isqrt(dx * dx + dy * dy);
      if (d <= p.speed || p.t > 60) {
        p.x = p.tx;
        p.y = p.ty;
        impact(s, p, e && e.hp > 0 ? e : null);
        continue;
      }
      p.x += idiv(dx * p.speed, d);
      p.y += idiv(dy * p.speed, d);
      keep.push(p);
      continue;
    }
    // Ground-targeted (arcing) projectile: linear over a fixed flight time.
    p.x = p.sx + idiv((p.tx - p.sx) * p.t, p.flight);
    p.y = p.sy + idiv((p.ty - p.sy) * p.t, p.flight);
    if (p.t >= p.flight) {
      impact(s, p, null);
      continue;
    }
    keep.push(p);
  }
  s.projectiles = keep;
};

const impact = (s: BattleState, p: Projectile, e: Enemy | null): void => {
  // A hero's basic attack carries its gear effects.
  const h = p.hero ? findHero(s, p.hero) : null;
  // A hero's shot is what its class deals; a tower's or a skill's, what its projectile is.
  // A conversion glyph's type first (docs/GLIFOS.md §1).
  const type = p.dt ?? (h ? ((h.blessed ?? 0) > 0 ? 'holy' : HERO_DMG_TYPE[h.cls]) : PROJECTILE_DMG_TYPE[p.kind]) ?? 'physical';
  if (p.splash > 0) {
    const ground = p.kind === 'rock' || p.kind === 'boulder';
    const hits = enemiesNear(s, p.x, p.y, p.splash, (o) => targetable(s, o) && (!ground || !isFlying(o)));
    for (const o of hits) {
      if (p.kind === 'fireball') o.scorch = s.tick;
      // A trebuchet's stone falls hardest on the big ones.
      const dmg = p.kind === 'boulder' && isHeavy(o) ? mulBp(p.damage, TREBUCHET.heavyBp) : p.damage;
      const dealt = hitWithEnchant(s, o, dmg, p.enchant, p.ignoresArmor, p.pierce, p.pid, p.crit, type, p.sx, p.sy);
      if (h) {
        gearOnHit(s, h, o, dealt);
        if (o.id === p.target) gearAfterHit(s, h, o, dealt);
        lampHeal(s, h, dealt);
      }
    }
    ev(s, { t: 'boom', x: p.x, y: p.y, r: p.splash, kind: p.kind });
    return;
  }
  if (!e) {
    if (p.uq) arrowLanded(s, p, null);
    return;
  }
  // A turret's bolt (no hero's own shot): a dry blow on the dragon's glass.
  const dealt = hitWithEnchant(s, e, p.damage, p.enchant, p.ignoresArmor, p.pierce, p.pid, p.crit, type, p.sx, p.sy, !h && p.kind === 'bullet');
  if (h) {
    gearOnHit(s, h, e, dealt);
    gearAfterHit(s, h, e, dealt);
    lampHeal(s, h, dealt);
    heroHitExtras(s, h, e, dealt);
  }
  if (p.stun > 0 && e.hp > 0) {
    // A steel net holds warlords too, for half (never bosses or the Warden).
    const st = p.hardStun && e.elite && !isBoss(e) && e.kind !== 'warden' ? idiv(p.stun, 2) : stunFor(e, p.stun);
    if (e.stun < st) e.stun = st;
  }
  // A hunter's bow arrow (docs/PANOPLIA.md §10): one that killed jumps.
  if (p.uq) arrowLanded(s, p, e);
};

/** A standing hero within `reach` of a tower (Beside, a bet). */
export const manned = (s: BattleState, st: Structure, reach: number): boolean => {
  const c = hexToWorld(st.q, st.r);
  return s.heroes.some((h) => h.downTicks === 0 && withinRange(h.x, h.y, c.x, c.y, reach));
};
